import React from "react";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Image from "@insightfulscience/atomic-react/Image";
import {
	PostElementLine,
	PostElementListItem,
	PostElementParagraph,
	PostElementUl,
} from "@insightfulscience/atomic-react/PostElement";

import Link from "@insightfulscience/atomic-react/Link";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroBackground from "@insightfulscience/atomic-react/Hero/HeroBackground";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import Feature from "@insightfulscience/atomic-react/Feature";
import LandingSimpleSection from "@insightfulscience/atomic-react/r2/LandingElement/SimpleSection";
import LandingSimpleSectionStripped from "@insightfulscience/atomic-react/LandingElement/SimpleSectionStripped";
import Box from "@insightfulscience/atomic-react/Box";
import { useLocale } from "@insightfulscience/smart-react/i18";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "../../SiteLayout";
import InvolveYourTeam from "../../InvolveYourTeam";
import WithServices from "../../services";
import SectionStart30DayTrial from "../../SectionStart30DayTrial";
import withErrorHandling from "../../AppError/withErrorHandling";

const PrismAnova = () => {
	const [t] = useLocale("prism-anova");
	return (
		<SiteLayout>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.Canonical href="https://www.graphpad.com/features/prism-anova" />
			</PageMeta>
			<Hero color="gradient" align="left">
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
				<HeroDescription>{t("HERO_DESCRIPTION")}</HeroDescription>
				<HeroAction center={false}>
					<HeroRow center={false}>
						<HeroCol shrink>
							<HeroButtonLink href="/demos/">{t("START_A_FREE_TRIAL")}</HeroButtonLink>
						</HeroCol>
						<HeroCol shrink>
							<HeroButtonLink href="/how-to-buy/" isOutline2 styleType="info-light">
								{t("PRICING_BUTTON")}
							</HeroButtonLink>
						</HeroCol>
					</HeroRow>
				</HeroAction>
				<HeroBackground>
					<Image
						src="/images/graphpad/survival-analysis/graphic-step-graph-576.png"
						className="h-60"
					/>
				</HeroBackground>
			</Hero>
			<LandingSimpleSectionStripped>
				<Row mx my utility={{ justify: "center" }}>
					<Col sm={12} md={6}>
						<PostElementParagraph>{t("LEFT_BULLET_ELEMENT_1")}</PostElementParagraph>
						<PostElementParagraph>{t("LEFT_BULLET_ELEMENT_2")}</PostElementParagraph>
						<PostElementParagraph>{t("LEFT_BULLET_ELEMENT_3")}</PostElementParagraph>
					</Col>
					<Col sm={12} md={6}>
						<Box utility={{ bgColor: "black--02" }} spacing={{ py: 4, px: 8 }}>
							<PostElementParagraph>{t("RIGHT_BULLET_ELEMENT_1")}</PostElementParagraph>
							<PostElementUl>
								<PostElementListItem>{t("RIGHT_BULLET_ELEMENT_2")}</PostElementListItem>
								<PostElementListItem>{t("RIGHT_BULLET_ELEMENT_3")}</PostElementListItem>
								<PostElementListItem>{t("RIGHT_BULLET_ELEMENT_4")}</PostElementListItem>
							</PostElementUl>
						</Box>
					</Col>
				</Row>
			</LandingSimpleSectionStripped>
			<LandingSimpleSectionStripped isDark>
				<LandingSimpleSection.TitleV2 sx={{ textAlign: "center" }}>
					{t("RUN_ALL_ANOVA_TITLE")}
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.DescriptionV2>
					{t("RUN_ALL_ANOVA_SUBTITLE")}
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Row mx my utility={{ justify: "center" }}>
						<Col sm={12} md={6}>
							<Box utility={{ bgColor: "white" }} shadow>
								<Image src="/images/graphpad/anova/repeated-measures-anova.gif" />
							</Box>
						</Col>
						<Col sm={12} md={6}>
							<Box utility={{ bgColor: "white" }} shadow>
								<Image src="/images/graphpad/anova/viral-infections-net-levels.gif" />
							</Box>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<LandingSimpleSectionStripped>
				<LandingSimpleSection.TitleV2>{t("PERFORMING_ANOVA_TITLE")}</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.DescriptionV2>
					{t("PERFORMING_ANOVA_SUBTITLE")}
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Feature top>
						<FeatureMeta numberItem="1">
							<FeatureMeta.Title>{t("STEPPER_FIRST_STEP_TITLE")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								{t("STEPPER_FIRST_STEP_DESCRIPTION_1")}
							</FeatureMeta.Description>
							<FeatureMeta.Description>
								{t("STEPPER_FIRST_STEP_DESCRIPTION_2")}
							</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage
							src="/images/graphpad/anova/ANOVA-Step-1-w800-trimmed-NON-curved-path.jpg"
							shadow
						/>
					</Feature>
					<Feature top>
						<FeatureMeta numberItem="2">
							<FeatureMeta.Title>{t("STEPPER_SECOND_STEP_TITLE")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								{t("STEPPER_SECOND_STEP_DESCRIPTION")}
							</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage
							src="/images/graphpad/anova/ANOVA-Step-2-w800-trimmed-curved-path.png.jpg"
							shadow
						/>
					</Feature>
					<Feature top>
						<FeatureMeta numberItem="3">
							<FeatureMeta.Title>{t("STEPPER_THIRD_STEP_TITLE")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								{t("STEPPER_THIRD_STEP_DESCRIPTION_1")}
							</FeatureMeta.Description>
							<FeatureMeta.Description>
								{t("STEPPER_THIRD_STEP_DESCRIPTION_2")}
							</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage
							src="/images/graphpad/anova/Anova-Step-3-in-Prism-w800-4-Graphs-Rectangle.jpg"
							shadow
						/>
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<InvolveYourTeam />
			<LandingSimpleSectionStripped>
				<PostElementLine />
				<LandingSimpleSection.TitleV2>{t("ACCESS_OTHER_TITLE")}</LandingSimpleSection.TitleV2>
				<Row mx my utility={{ justify: "center" }} spacing={{ my: 10 }}>
					<Col sm={12} md={4}>
						<Link href="/series/essential-statistics/#alfeeuz31r">
							<Image className="w-100" src="/images/graphpad/anova/1.jpg" />
						</Link>
					</Col>
					<Col sm={12} md={4}>
						<Link href="/guides/the-ultimate-guide-to-anova">
							<Image className="w-100" src="/images/graphpad/anova/2.jpg" />
						</Link>
					</Col>
					<Col sm={12} md={4}>
						<Link href="/series/essential-statistics/#7j8etfigfu">
							<Image className="w-100" src="/images/graphpad/anova/3.jpg" />
						</Link>
					</Col>
				</Row>
			</LandingSimpleSectionStripped>
			<SectionStart30DayTrial />
		</SiteLayout>
	);
};

export default pipe(PrismAnova, withErrorHandling, WithServices);
